import { useState, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, Link } from '@tanstack/react-router'
import { 
  Button, 
  Heading, 
  Text,
  Container,
  Section,
  SelectionCard,
  QuantitySelector,
  SummaryBar,
} from '@shinkansen/sakura'
import { useCreateOrder } from '../api/orders'
import { useMyAddresses, useDeliveryEstimate, type Address } from '../api/addresses'
import { usePricingSettings } from '../api/settings'
import { useAuth } from '../auth'
import { LocationPicker, DeliveryEstimateDisplay, type Location } from '../components/map'
import { CHECKOUT_PAYMENT_METHODS } from './order-new.payment-methods'
import { getDeliveryCheckoutError } from './order-new.validation'
import styles from './order-new.module.css'

type EggSize = 'small' | 'medium' | 'large'
type PackType = 'pack' | 'qty'

interface EggConfig {
  size: EggSize
  packType: PackType
  quantity: number
}

// Default pricing fallback - will be overridden by API data
const DEFAULT_EGG_SIZES: { id: EggSize; label: string; weight: string; price: number }[] = [
  { id: 'small', label: 'SMALL', weight: '50-55g', price: 2500 },
  { id: 'medium', label: 'MEDIUM', weight: '55-60g', price: 3000 },
  { id: 'large', label: 'LARGE', weight: '60-65g', price: 3500 },
]

const PACK_TYPES: { id: PackType; label: string; unit: string; min: number; eggs: number; desc: string }[] = [
  { id: 'pack', label: 'PACK', unit: 'PACKS', min: 1, eggs: 30, desc: 'Bulk tray of 30 eggs' },
  { id: 'qty', label: 'INDIVIDUAL', unit: 'EGGS', min: 6, eggs: 1, desc: 'Pick your own count' },
]

const PRODUCT_IDS: Record<EggSize, string> = {
  small: '550e8400-e29b-41d4-a716-446655440001',
  medium: '550e8400-e29b-41d4-a716-446655440002',
  large: '550e8400-e29b-41d4-a716-446655440003',
}

type AddressMode = 'saved' | 'new'

export function OrderNewPage() {
  const navigate = useNavigate()
  const { isAuthenticated, login, user } = useAuth()
  const createOrder = useCreateOrder()
  const { data: savedAddresses, isLoading: addressesLoading } = useMyAddresses()
  const { data: pricingSettings } = usePricingSettings()
  
  // Dynamic egg sizes based on pricing settings from API
  const EGG_SIZES = useMemo(() => {
    if (!pricingSettings) return DEFAULT_EGG_SIZES
    return [
      { id: 'small' as EggSize, label: 'SMALL', weight: '50-55g', price: pricingSettings.eggPrices.small },
      { id: 'medium' as EggSize, label: 'MEDIUM', weight: '55-60g', price: pricingSettings.eggPrices.medium },
      { id: 'large' as EggSize, label: 'LARGE', weight: '60-65g', price: pricingSettings.eggPrices.large },
    ]
  }, [pricingSettings])
  
  const [config, setConfig] = useState<EggConfig>({
    size: 'medium',
    packType: 'pack',
    quantity: 1,
  })
  const [addressMode, setAddressMode] = useState<AddressMode>('saved')
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)
  const [newDeliveryLocation, setNewDeliveryLocation] = useState<Location | undefined>()
  const [payerEmailOverride, setPayerEmailOverride] = useState<string | null>(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('qris')
  const [formError, setFormError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ delivery?: string; email?: string }>({})

  const defaultSavedAddress = useMemo(() => {
    if (!savedAddresses?.length) return undefined

    const defaultAddress = savedAddresses.find(address => address.isDefault)
    if (defaultAddress) return defaultAddress
    return savedAddresses[0]
  }, [savedAddresses])
  const selectedSavedAddress = useMemo(() => {
    if (!selectedAddressId || !savedAddresses) return defaultSavedAddress

    const selectedAddress = savedAddresses.find(address => address.id === selectedAddressId)
    if (selectedAddress) return selectedAddress
    return defaultSavedAddress
  }, [savedAddresses, selectedAddressId, defaultSavedAddress])
  const savedDeliveryLocation = useMemo<Location | undefined>(() => {
    if (!selectedSavedAddress) return undefined

    return {
      lat: selectedSavedAddress.coordinates.lat,
      lng: selectedSavedAddress.coordinates.lng,
      address: selectedSavedAddress.fullAddress,
      notes: selectedSavedAddress.notes === null ? undefined : selectedSavedAddress.notes,
    }
  }, [selectedSavedAddress])
  const deliveryLocation = addressMode === 'saved' ? savedDeliveryLocation : newDeliveryLocation
  let payerEmail = ''
  if (payerEmailOverride !== null) {
    payerEmail = payerEmailOverride
  } else if (user?.email) {
    payerEmail = user.email
  }

  const handleSelectSavedAddress = (address: Address) => {
    setSelectedAddressId(address.id)
    setFieldErrors(prev => ({ ...prev, delivery: undefined }))
    setFormError(null)
    setAddressMode('saved')
  }

  const handleUseNewAddress = () => {
    setAddressMode('new')
    setSelectedAddressId(null)
    setNewDeliveryLocation(undefined)
    setFieldErrors(prev => ({ ...prev, delivery: undefined }))
    setFormError(null)
  }

  const handleBackToSaved = () => {
    setAddressMode('saved')
    if (defaultSavedAddress) {
      handleSelectSavedAddress(defaultSavedAddress)
    }
  }

  const hasNoSavedAddresses = !addressesLoading && (!savedAddresses || savedAddresses.length === 0)

  const selectedSize = EGG_SIZES.find(s => s.id === config.size)!
  const selectedPackType = PACK_TYPES.find(p => p.id === config.packType)!
  
  // Get delivery estimate for the selected location
  const {
    data: deliveryEstimate,
    isError: isDeliveryEstimateError,
    isFetching: isDeliveryEstimateFetching,
    isLoading: isDeliveryEstimateLoading,
  } = useDeliveryEstimate(
    deliveryLocation?.lat ?? 0,
    deliveryLocation?.lng ?? 0,
    !!deliveryLocation
  )
  
  const totalEggs = config.quantity * selectedPackType.eggs
  const subtotal = totalEggs * selectedSize.price
  const deliveryFee = deliveryEstimate?.isDeliverable ? deliveryEstimate.fee : 0
  const totalPrice = subtotal + deliveryFee
  const selectedPayment =
    CHECKOUT_PAYMENT_METHODS.find((method) => method.id === selectedPaymentMethod) ??
    CHECKOUT_PAYMENT_METHODS[0]
  const isDeliveryEstimatePending =
    !!deliveryLocation && !deliveryEstimate && (isDeliveryEstimateLoading || isDeliveryEstimateFetching)

  const handleQuantityChange = useCallback((newQty: number) => {
    setConfig(prev => ({ ...prev, quantity: newQty }))
  }, [])

  const handlePackTypeChange = useCallback((packType: PackType) => {
    const pack = PACK_TYPES.find(p => p.id === packType)!
    setConfig(prev => ({
      ...prev,
      packType,
      quantity: pack.min,
    }))
  }, [])

  const handleDeliveryLocationChange = useCallback((location: Location | undefined) => {
    setNewDeliveryLocation(location)
    if (location) {
      setAddressMode('new')
      setSelectedAddressId(null)
      setFieldErrors(prev => ({ ...prev, delivery: undefined }))
      setFormError(null)
    }
  }, [])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      login()
      return
    }

    const nextFieldErrors: { delivery?: string; email?: string } = {}
    const deliveryError = getDeliveryCheckoutError({
      hasDeliveryLocation: !!deliveryLocation,
      deliveryEstimate,
      isDeliveryEstimatePending,
      isDeliveryEstimateError,
    })
    if (deliveryError) nextFieldErrors.delivery = deliveryError

    if (!payerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerEmail.trim())) {
      nextFieldErrors.email = 'Enter a valid email address for the payment receipt.'
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors)
      setFormError('Fix the highlighted checkout fields before placing the order.')
      return
    }

    const orderDeliveryLocation = deliveryLocation
    if (!orderDeliveryLocation) return

    try {
      setFieldErrors({})
      setFormError(null)
      const order = await createOrder.mutateAsync({
        items: [{
          productId: PRODUCT_IDS[config.size],
          productName: `${selectedSize.label} Eggs`,
          quantity: totalEggs,
          unitPrice: selectedSize.price,
        }],
        deliveryAddress: orderDeliveryLocation.address,
        deliveryLat: orderDeliveryLocation.lat,
        deliveryLng: orderDeliveryLocation.lng,
        deliveryFee,
        notes: orderDeliveryLocation.notes,
        paymentSelection: selectedPayment.selection,
        payerEmail,
      })
      
      if (order?.id) {
        navigate({ to: '/tracking/$orderId', params: { orderId: order.id }, search: { payment: 'pending' } })
      } else {
        navigate({ to: '/orders' })
      }
    } catch (error) {
      console.error('Failed to create order:', error)
      setFormError(error instanceof Error ? error.message : 'Failed to create order. Please try again.')
    }
  }

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <header className={styles.hero}>
        <Container size="xl">
          {formError && (
            <div className={styles.formError} role="alert">
              {formError}
            </div>
          )}

          <div className={styles.heroGrid}>
            <div className={styles.heroContent}>
              <Text size="xs" variant="muted" className={styles.edition}>
                TASAMAGO • FRESH EGGS • EST. 2025
              </Text>
              
              <Heading level="h1" size="hero" className={styles.heroTitle}>
                ORDER<br />
                <span className={styles.highlight}>FRESH EGGS</span>
              </Heading>
              
              <Text size="lg" variant="muted" className={styles.heroSubtitle}>
                Premium farm-fresh eggs delivered straight to your door. 
                Quality you can taste.
              </Text>

              <div className={styles.heroStats}>
                <div className={styles.stat}>
                  <span className={styles.statValue}>100%</span>
                  <span className={styles.statLabel}>Farm Fresh</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>24H</span>
                  <span className={styles.statLabel}>Delivery</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>30D</span>
                  <span className={styles.statLabel}>Guarantee</span>
                </div>
              </div>
            </div>

            <div className={styles.heroEgg}>
              <motion.div 
                className={styles.eggIllustration}
                animate={{ 
                  y: [0, -15, 0],
                  rotate: [-2, 2, -2],
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity,
                  ease: "easeInOut" 
                }}
              />
            </div>
          </div>
        </Container>
      </header>

      {/* Order Configurator */}
      <main className={styles.configurator}>
        <Container size="xl">
          {/* Section 1: Size */}
          <Section number="01" label="Select Grade" title="Egg Size" border="none">
            <div className={styles.sizeGrid}>
              {EGG_SIZES.map((size, index) => (
                <SelectionCard
                  key={size.id}
                  label={`0${index + 1}`}
                  selected={config.size === size.id}
                  onClick={() => setConfig(prev => ({ ...prev, size: size.id }))}
                  className={styles.sizeCard}
                >
                  <div className={styles.sizeCardContent}>
                    <motion.div 
                      className={styles.eggVisual}
                      whileHover={{ scale: 1.1 }}
                    >
                      <div 
                        className={styles.egg}
                        data-selected={config.size === size.id}
                        style={{ 
                          width: size.id === 'small' ? 50 : size.id === 'medium' ? 65 : 80,
                          height: size.id === 'small' ? 62 : size.id === 'medium' ? 81 : 100,
                        }}
                      />
                    </motion.div>
                    
                    <div className={styles.sizeInfo}>
                      <span className={styles.sizeName}>{size.label}</span>
                      <span className={styles.sizeWeight}>{size.weight}</span>
                    </div>
                    
                    <div className={styles.sizePrice}>
                      <span className={styles.priceValue}>{formatPrice(size.price)}</span>
                      <span className={styles.priceUnit}>/egg</span>
                    </div>
                  </div>
                </SelectionCard>
              ))}
            </div>
          </Section>

          {/* Section 2: Quantity */}
          <Section number="02" label="How Many" title="Quantity">
            <div className={styles.quantitySection}>
              {/* Pack Type Toggle */}
              <div className={styles.packToggle}>
                {PACK_TYPES.map((pack) => (
                  <button
                    key={pack.id}
                    type="button"
                    className={styles.packToggleBtn}
                    data-selected={config.packType === pack.id}
                    onClick={() => handlePackTypeChange(pack.id)}
                  >
                    <span className={styles.packToggleLabel}>{pack.label}</span>
                    <span className={styles.packToggleDesc}>
                      {pack.id === 'pack' ? '30 eggs/pack' : 'Min 6 eggs'}
                    </span>
                  </button>
                ))}
              </div>

              {/* Quantity Selector + Total */}
              <div className={styles.quantityWrapper}>
                <QuantitySelector
                  value={config.quantity}
                  unit={selectedPackType.unit}
                  min={selectedPackType.min}
                  onChange={handleQuantityChange}
                  renderValue={(val) => (
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={val}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        {val}
                      </motion.span>
                    </AnimatePresence>
                  )}
                />
                
                <div className={styles.totalEggsBox}>
                  <span className={styles.totalEggsLabel}>Total Eggs</span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={totalEggs}
                      className={styles.totalEggsValue}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      {totalEggs}
                    </motion.span>
                  </AnimatePresence>
                  <span className={styles.totalEggsUnit}>eggs</span>
                </div>
              </div>
            </div>
          </Section>

          {/* Section 3: Delivery Location */}
          <Section number="03" label="Delivery Info" title="Location">
            <div className={styles.deliverySection}>
              {/* No saved addresses - prompt to add one */}
              {hasNoSavedAddresses && isAuthenticated && (
                <div className={styles.noAddressPrompt}>
                  <div className={styles.noAddressIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <Text size="lg">NO DELIVERY ADDRESS</Text>
                  <Text variant="muted">Add a delivery address to continue with your order</Text>
                  <Link to="/addresses" className={styles.addAddressLink}>
                    <Button variant="primary">
                      ADD DELIVERY ADDRESS
                    </Button>
                  </Link>
                </div>
              )}

              {/* Has saved addresses - show selection or picker */}
              {!hasNoSavedAddresses && isAuthenticated && (
                <>
                  {addressMode === 'saved' ? (
                    <div className={styles.savedAddressSection}>
                      <div className={styles.addressSelectorHeader}>
                        <span className={styles.addressSelectorLabel}>DELIVER TO</span>
                        <button 
                          type="button"
                          className={styles.useNewAddressBtn}
                          onClick={handleUseNewAddress}
                        >
                          USE DIFFERENT ADDRESS
                        </button>
                      </div>
                      
                      <div className={styles.savedAddressList}>
                        {savedAddresses?.map((address) => (
                          <button
                            key={address.id}
                            type="button"
                            className={styles.savedAddressCard}
                            data-selected={selectedSavedAddress?.id === address.id}
                            onClick={() => handleSelectSavedAddress(address)}
                          >
                            <div className={styles.addressCardRadio}>
                              <div className={styles.radioOuter}>
                                {selectedSavedAddress?.id === address.id && (
                                  <div className={styles.radioInner} />
                                )}
                              </div>
                            </div>
                            <div className={styles.addressCardContent}>
                              <div className={styles.addressCardHeader}>
                                {address.label && (
                                  <span className={styles.addressLabel}>{address.label}</span>
                                )}
                                {address.isDefault && (
                                  <span className={styles.defaultBadge}>DEFAULT</span>
                                )}
                              </div>
                              <p className={styles.addressText}>{address.fullAddress}</p>
                              {address.notes && (
                                <p className={styles.addressNotes}>{address.notes}</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>

                      <Link to="/addresses" className={styles.manageAddressesLink}>
                        MANAGE ADDRESSES
                      </Link>
                    </div>
                  ) : (
                    <div className={styles.newAddressSection}>
                      <div className={styles.newAddressHeader}>
                        <span className={styles.newAddressLabel}>ONE-TIME DELIVERY ADDRESS</span>
                        {savedAddresses && savedAddresses.length > 0 && (
                          <button 
                            type="button"
                            className={styles.backToSavedBtn}
                            onClick={handleBackToSaved}
                          >
                            USE SAVED ADDRESS
                          </button>
                        )}
                      </div>
                      <LocationPicker
                        value={deliveryLocation}
                        onChange={handleDeliveryLocationChange}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Not authenticated - show LocationPicker */}
              {!isAuthenticated && (
                <LocationPicker
                  value={deliveryLocation}
                  onChange={handleDeliveryLocationChange}
                />
              )}
              
              <DeliveryEstimateDisplay
                lat={deliveryLocation?.lat}
                lng={deliveryLocation?.lng}
              />
              {fieldErrors.delivery && (
                <div className={styles.fieldError}>{fieldErrors.delivery}</div>
              )}
            </div>
          </Section>

          {/* Section 4: Payment Email */}
          {isAuthenticated && (
            <Section number="04" label="Payment" title="Email">
              <div className={styles.emailSection}>
                <Text size="sm" variant="muted" className={styles.emailLabel}>
                  Payment receipt will be sent to this email
                </Text>
                <input
                  type="email"
                  value={payerEmail}
                  onChange={(e) => {
                    setPayerEmailOverride(e.target.value)
                    setFieldErrors(prev => ({ ...prev, email: undefined }))
                    setFormError(null)
                  }}
                  placeholder="your@email.com"
                  className={`${styles.emailInput} ${fieldErrors.email ? styles.inputInvalid : ''}`}
                  aria-invalid={fieldErrors.email ? true : undefined}
                  aria-describedby={fieldErrors.email ? 'payer-email-error' : undefined}
                  required
                />
                {fieldErrors.email && (
                  <div id="payer-email-error" className={styles.fieldError}>{fieldErrors.email}</div>
                )}
              </div>
            </Section>
          )}

          {/* Section 5: Payment Method */}
          {isAuthenticated && (
            <Section number="05" label="Payment" title="Method">
              <div className={styles.paymentSection}>
                <div className={styles.paymentGrid}>
                  {CHECKOUT_PAYMENT_METHODS.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      className={styles.paymentCard}
                      data-selected={selectedPaymentMethod === method.id}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                    >
                      <span className={styles.paymentMeta}>{method.meta}</span>
                      <span className={styles.paymentLabel}>{method.label}</span>
                      <span className={styles.paymentDescription}>{method.description}</span>
                    </button>
                  ))}
                </div>
                <div className={styles.paymentNotice}>
                  {selectedPayment.label} selected. The active provider will return the exact payment instructions after the order is placed.
                </div>
              </div>
            </Section>
          )}
        </Container>
      </main>

      {/* Fixed Summary Bar */}
      <SummaryBar
        brand="TASAMAGO"
        items={[
          { label: 'Size', value: selectedSize.label },
          { label: 'Eggs', value: String(totalEggs) },
          { label: 'Subtotal', value: formatPrice(subtotal) },
          { label: 'Delivery', value: deliveryFee > 0 ? formatPrice(deliveryFee) : '-' },
        ]}
        total={{
          label: 'Total',
          value: formatPrice(totalPrice),
        }}
        action={
          <Button
            variant="primary"
            size="lg"
            onClick={handlePlaceOrder}
            disabled={createOrder.isPending || (isAuthenticated && isDeliveryEstimatePending)}
            style={{ 
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-accent-foreground)',
              borderColor: 'var(--color-accent)',
            }}
          >
            {createOrder.isPending 
              ? 'Placing...'
              : isAuthenticated && isDeliveryEstimatePending
                ? 'Checking Delivery...'
              : isAuthenticated 
                ? 'Place Order'
                : 'Login to Order'
            }
          </Button>
        }
      />
    </div>
  )
}
